/**
 * Client-side contract for the industry ad signup pages.
 *
 * The backend re-validates every field. These helpers exist so the mobile
 * form can explain a bad website or phone before the request, and so the
 * ad click (UTMs, fbclid) survives the password step into both the lead
 * and the new workspace.
 */

import { GENERIC_CAMPAIGN, SIGNUP_CAMPAIGNS } from "../data/signupCampaigns";

/** Must match `DOMAIN_REGEX` in backend/src/utils/validation.ts. */
const DOMAIN_REGEX = /^(?=.{1,50}$)(?!-)(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,63}$/i;
const SHOP_DOMAIN_REGEX = /\.myshopify\.com$/i;
const NAME_REGEX = /^[A-Za-z][A-Za-z .'-]*$/;
const ORG_NAME_REGEX = /^[A-Za-z0-9][A-Za-z0-9 .&'()-]*$/;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export interface WebsiteNormalization {
    ok: boolean;
    domain: string | null;
    error?: string;
}

export function normalizeWebsite(raw: string): WebsiteNormalization {
    const trimmed = raw.trim();
    if (!trimmed) return { ok: true, domain: null };

    let host = trimmed.replace(/^https?:\/\//i, "");
    host = host.split(/[/?#]/)[0] ?? "";
    host = host.replace(/:\d+$/, "");
    host = host.replace(/^www\./i, "").replace(/\.+$/, "").toLowerCase();

    if (!host || !DOMAIN_REGEX.test(host)) {
        return { ok: false, domain: null, error: "Enter a website like example.com, or leave it blank." };
    }
    if (SHOP_DOMAIN_REGEX.test(host)) {
        return { ok: false, domain: null, error: "That's a Shopify store domain. Install FloGPT from the Shopify App Store instead." };
    }
    return { ok: true, domain: host };
}

export function splitFullName(raw: string): { firstName: string; lastName: string } | null {
    const parts = raw.trim().replace(/\s+/g, " ").split(" ");
    if (parts.length < 2) return null;
    const firstName = parts[0];
    const lastName = parts.slice(1).join(" ");
    if (firstName.length < 2 || firstName.length > 50 || !NAME_REGEX.test(firstName)) return null;
    if (lastName.length < 2 || lastName.length > 50 || !NAME_REGEX.test(lastName)) return null;
    return { firstName, lastName };
}

export function normalizePhone(raw: string): string | null {
    const trimmed = raw.trim();
    if (!trimmed) return null;
    const digits = trimmed.replace(/\D/g, "");
    let e164 = "";
    if (trimmed.startsWith("+")) e164 = `+${digits}`;
    else if (digits.length === 10) e164 = `+1${digits}`;
    else if (digits.length === 11 && digits.startsWith("1")) e164 = `+${digits}`;
    else e164 = `+${digits}`;
    return /^\+[1-9]\d{6,14}$/.test(e164) ? e164 : null;
}

export function normalizeEmail(raw: string): string | null {
    const email = raw.trim().toLowerCase();
    if (!email || email.length > 254 || !EMAIL_RE.test(email)) return null;
    return email;
}

export function normalizeBusinessName(raw: string): string | null {
    const name = raw.trim().replace(/\s+/g, " ");
    if (name.length < 2 || name.length > 50 || !ORG_NAME_REGEX.test(name)) return null;
    return name;
}

export interface SignupAttribution {
    utmSource: string | null;
    utmMedium: string | null;
    utmCampaign: string | null;
    utmTerm: string | null;
    utmContent: string | null;
    fbclid: string | null;
}

const ATTRIBUTION_KEY = "signupAttribution";
const UTM_PARAMS: Array<[keyof SignupAttribution, string]> = [
    ["utmSource", "utm_source"],
    ["utmMedium", "utm_medium"],
    ["utmCampaign", "utm_campaign"],
    ["utmTerm", "utm_term"],
    ["utmContent", "utm_content"],
    ["fbclid", "fbclid"],
];

function emptyAttribution(): SignupAttribution {
    return { utmSource: null, utmMedium: null, utmCampaign: null, utmTerm: null, utmContent: null, fbclid: null };
}

function clip(value: string | null): string | null {
    const trimmed = (value || "").trim();
    if (!trimmed || trimmed === "undefined" || trimmed === "null") return null;
    return trimmed.slice(0, 200);
}

/**
 * Merge the current URL's ad params over anything already stored.
 *
 * The password step is a client navigation that does not have to keep the
 * query string, and a refresh on the form must not drop `fbclid`.
 */
export function captureAttribution(search: string): SignupAttribution {
    const params = new URLSearchParams(search.startsWith("?") ? search.slice(1) : search);
    let stored = emptyAttribution();
    try {
        const raw = sessionStorage.getItem(ATTRIBUTION_KEY);
        if (raw) stored = { ...stored, ...JSON.parse(raw) };
    } catch {
        stored = emptyAttribution();
    }
    const fromUrl = emptyAttribution();
    for (const [field, param] of UTM_PARAMS) {
        fromUrl[field] = clip(params.get(param));
    }
    const merged = emptyAttribution();
    for (const [field] of UTM_PARAMS) {
        merged[field] = fromUrl[field] || clip(stored[field]);
    }
    try {
        sessionStorage.setItem(ATTRIBUTION_KEY, JSON.stringify(merged));
    } catch {
        /* private mode */
    }
    return merged;
}

/**
 * What the signup told us, handed to `/onboarding` and `/call/setup` so the
 * guided setup does not ask the same questions a second time.
 */
export interface SignupIntent {
    businessName: string;
    /** Campaign id — an industry id, or `any`. */
    campaign: string;
    /** The resolved vertical. Always a real industry id. */
    industry: string;
    website: string | null;
    domain: string;
    ts: number;
}

const INTENT_KEY = "signupIntent";
const MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;

const CAMPAIGN_IDS = new Set(SIGNUP_CAMPAIGNS.map((campaign) => campaign.id));
const INDUSTRY_IDS = new Set(
    SIGNUP_CAMPAIGNS.map((campaign) => campaign.industry).filter((id): id is string => Boolean(id)),
);

export function writeSignupIntent(intent: Omit<SignupIntent, "ts">): void {
    try {
        const payload: SignupIntent = { ...intent, ts: Date.now() };
        localStorage.setItem(INTENT_KEY, JSON.stringify(payload));
    } catch {
        /* private mode — onboarding falls back to the generic loader */
    }
}

export function readSignupIntent(): SignupIntent | null {
    try {
        const raw = localStorage.getItem(INTENT_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw) as Partial<SignupIntent>;
        const businessName = typeof parsed.businessName === "string" ? parsed.businessName.trim() : "";
        const campaign = typeof parsed.campaign === "string" ? parsed.campaign : "";
        const industry = typeof parsed.industry === "string" ? parsed.industry : "";
        const ts = typeof parsed.ts === "number" ? parsed.ts : 0;
        if (!businessName || !CAMPAIGN_IDS.has(campaign) || !INDUSTRY_IDS.has(industry)) return null;
        if (!ts || Date.now() - ts > MAX_AGE_MS) return null;
        return {
            businessName,
            campaign,
            industry,
            website: typeof parsed.website === "string" && parsed.website ? parsed.website : null,
            domain: typeof parsed.domain === "string" ? parsed.domain : "",
            ts,
        };
    } catch {
        return null;
    }
}

export function clearSignupIntent(): void {
    try {
        localStorage.removeItem(INTENT_KEY);
    } catch {
        /* nothing to clear */
    }
}

/**
 * Canonical ad URL. `/home-services` is the one legacy alias that predates
 * the `/get-started` prefix and must keep the click params.
 */
export function signupPath(campaignSlug: string, search = "", hash = ""): string {
    const query = search ? (search.startsWith("?") ? search : `?${search}`) : "";
    const fragment = hash ? (hash.startsWith("#") ? hash : `#${hash}`) : "";
    const base = campaignSlug === GENERIC_CAMPAIGN ? "/get-started" : `/get-started/${campaignSlug}`;
    return `${base}${query}${fragment}`;
}

const LEAD_EVENT_PREFIX = "signup_lead_event:";
const LEAD_FIRED_PREFIX = "signup_lead_fired:";

export function leadEventIdFor(email: string): string {
    const key = `${LEAD_EVENT_PREFIX}${email}`;
    try {
        const existing = sessionStorage.getItem(key);
        if (existing) return existing;
        const id = crypto.randomUUID();
        sessionStorage.setItem(key, id);
        return id;
    } catch {
        return crypto.randomUUID();
    }
}

export function leadAlreadyFired(email: string): boolean {
    try {
        return sessionStorage.getItem(`${LEAD_FIRED_PREFIX}${email}`) === "1";
    } catch {
        return false;
    }
}

export function markLeadFired(email: string): void {
    try {
        sessionStorage.setItem(`${LEAD_FIRED_PREFIX}${email}`, "1");
    } catch {
        /* the event still fired; a refresh may send it again and Meta dedupes on event_id */
    }
}

function readCookie(name: string): string | null {
    if (typeof document === "undefined") return null;
    const prefix = `${name}=`;
    for (const part of document.cookie.split(";")) {
        const trimmed = part.trim();
        if (!trimmed.startsWith(prefix)) continue;
        const value = trimmed.slice(prefix.length);
        if (!value) return null;
        try {
            return decodeURIComponent(value);
        } catch {
            return value;
        }
    }
    return null;
}

/**
 * Meta's first-party cookies. The pixel sets them on the site host, and the
 * API is a different host, so the GraphQL request cannot see them unless the
 * page sends them.
 */
export function readMetaCookies(): { fbp: string | null; fbc: string | null } {
    return { fbp: readCookie("_fbp"), fbc: readCookie("_fbc") };
}
